<template>
  <div class="km-deposit">
    <!-- ===== TOP BAR ===== -->
    <header class="kd-top">
      <button class="kd-back" @click="goBack" aria-label="Back">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
      </button>
      <div class="kd-title">
        <span class="kd-dot"></span>
        <h1>Deposit</h1>
      </div>
      <button class="kd-ghost" @click="showHistory = !showHistory" aria-label="History">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5M12 7v5l3 3"/></svg>
      </button>
    </header>

    <main class="kd-main">
      <!-- ===== HERO BALANCE ===== -->
      <section class="kd-section">
        <div class="kd-balance">
          <div class="kd-balance__glow"></div>
          <div class="kd-balance__lbl">💎 Your Balance</div>
          <div class="kd-balance__val">{{ formatMoney(userInfo.money) }} <span>PT</span></div>
          <div class="kd-balance__sub">Top up with crypto to unlock premium features ✨</div>
        </div>
      </section>

      <!-- ===== PAY METHOD TABS ===== -->
      <section class="kd-section">
        <div class="kd-paytabs">
          <button class="kd-paytab" :class="{ active: payTab==='crypto' }" @click="payTab='crypto'">🪙 Crypto</button>
          <button class="kd-paytab" :class="{ active: payTab==='card' }"   @click="payTab='card'">💳 Debit / Credit Card</button>
        </div>
      </section>

      <!-- ===== METHOD SELECTOR (crypto only) ===== -->
      <section v-if="payTab==='crypto'" class="kd-section">
        <div class="kd-sec-head"><h3>1. Choose a crypto</h3></div>
        <div v-if="loadingMethods" class="kd-methods">
          <div v-for="n in 4" :key="'mk'+n" class="kd-method kd-method--skel"></div>
        </div>
        <div v-else-if="!methods.length" class="kd-empty">
          <div class="kd-empty__emoji">⏳</div>
          <p>No payment methods are configured yet. Please check back soon.</p>
        </div>
        <div v-else class="kd-methods">
          <button
            v-for="m in methods" :key="m.id"
            class="kd-method" :class="[{ active: selected && selected.id === m.id }, 'kd-method--'+m.color]"
            @click="selectMethod(m)"
          >
            <span class="kd-method__icon">{{ symbolIcon(m.symbol) }}</span>
            <div class="kd-method__body">
              <div class="kd-method__sym">{{ m.symbol }}</div>
              <div class="kd-method__net">{{ m.network }}</div>
            </div>
            <span v-if="selected && selected.id === m.id" class="kd-method__check">✓</span>
          </button>
        </div>
      </section>

      <!-- ===== QR + ADDRESS ===== -->
      <section v-if="payTab==='crypto' && selected" class="kd-section">
        <div class="kd-sec-head"><h3>2. Send {{ selected.symbol }} to this address</h3></div>
        <div class="kd-wallet">
          <div class="kd-wallet__qrwrap">
            <img :src="qrUrl(selected.address)" :alt="'QR for ' + selected.symbol" class="kd-wallet__qr"/>
            <div class="kd-wallet__qrbadge">{{ symbolIcon(selected.symbol) }}</div>
          </div>
          <div class="kd-wallet__info">
            <div class="kd-field">
              <span class="kd-field__lbl">Network</span>
              <div class="kd-field__val">
                <strong>{{ selected.network }}</strong>
                <span class="kd-pill">{{ selected.confirmations }} confirmation{{ selected.confirmations === 1 ? '' : 's' }}</span>
              </div>
            </div>
            <div class="kd-field">
              <span class="kd-field__lbl">Wallet address</span>
              <div class="kd-addr">
                <span class="kd-addr__text">{{ selected.address }}</span>
                <button class="kd-addr__copy" :class="{ copied: copied }" @click="copyAddress">
                  <svg v-if="!copied" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>
                  <svg v-else viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                  {{ copied ? 'Copied' : 'Copy' }}
                </button>
              </div>
            </div>
            <div class="kd-field" v-if="selected.memo">
              <span class="kd-field__lbl">Memo / Tag <span class="kd-req">required</span></span>
              <div class="kd-addr">
                <span class="kd-addr__text">{{ selected.memo }}</span>
                <button class="kd-addr__copy" @click="copyText(selected.memo)">Copy</button>
              </div>
            </div>
            <div class="kd-field kd-field--rate">
              <span>1 {{ selected.symbol }}</span>
              <span class="kd-field__eq">≈</span>
              <strong>{{ formatMoney(selected.rate) }} PT</strong>
            </div>
          </div>
        </div>
        <div class="kd-warn">
          <span>⚠️</span>
          Only send <strong>{{ selected.symbol }}</strong> on the <strong>{{ selected.network }}</strong> network.
          Sending other coins or using the wrong network will result in lost funds.
        </div>
      </section>

      <!-- ===== CONFIRM FORM ===== -->
      <section v-if="payTab==='crypto' && selected" class="kd-section">
        <div class="kd-sec-head"><h3>3. Confirm your deposit</h3></div>
        <div class="kd-form">
          <label class="kd-field">
            <span class="kd-field__lbl">Amount sent ({{ selected.symbol }})</span>
            <div class="kd-amount">
              <input
                v-model="amount"
                type="number"
                inputmode="decimal"
                :placeholder="'Min ' + trimZero(selected.min_amount) + ' ' + selected.symbol"
              />
              <span class="kd-amount__chip">{{ selected.symbol }}</span>
            </div>
            <span class="kd-field__hint kd-field__hint--err" v-if="amountError">{{ amountError }}</span>
            <span class="kd-field__hint" v-else-if="amount > 0">
              You'll receive <strong>{{ formatMoney(pointsPreview) }} PT</strong>
            </span>
            <span class="kd-field__hint" v-else>
              Min {{ trimZero(selected.min_amount) }} · Max {{ trimZero(selected.max_amount) }} {{ selected.symbol }}
            </span>
          </label>
          <label class="kd-field">
            <span class="kd-field__lbl">Transaction hash <span class="kd-field__opt">(optional but speeds things up)</span></span>
            <input v-model="txHash" type="text" placeholder="0x…"/>
          </label>
          <label class="kd-field">
            <span class="kd-field__lbl">Your wallet address <span class="kd-field__opt">(optional)</span></span>
            <input v-model="fromAddress" type="text" placeholder="Where you sent from"/>
          </label>
          <button type="button" class="kd-submit" @click.prevent="submit" :disabled="submitting" :aria-busy="submitting ? 'true' : 'false'">
            <span v-if="submitting"><span class="kd-spin"></span> Submitting…</span>
            <span v-else>I've sent it — credit my account</span>
          </button>
          <p class="kd-submit__note">
            Your points are credited as soon as the transaction gets <strong>{{ selected.confirmations }}</strong> on-chain confirmation{{ selected.confirmations === 1 ? '' : 's' }}.
          </p>
        </div>
      </section>

      <!-- ===== CARD PAYMENT FLOW (classy) ===== -->
      <section v-if="payTab==='card'" class="kd-card-flow">

        <!-- 1 · Premium card visual preview -->
        <div class="kd-cc-preview">
          <div class="kd-cc" :class="['kd-cc--'+(cardForm.brand ? cardForm.brand.toLowerCase() : 'neutral'), { 'kd-cc--filled': (cardForm.number || '').replace(/\s+/g,'').length >= 4 }]">
            <div class="kd-cc__shine"></div>
            <div class="kd-cc__top">
              <div class="kd-cc__chip">
                <div class="kd-cc__chip-lines"></div>
              </div>
              <div class="kd-cc__wifi">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 10a15 15 0 0 1 16 0M7 13.5a10 10 0 0 1 10 0M10 17a5 5 0 0 1 4 0"/></svg>
              </div>
            </div>
            <div class="kd-cc__num">{{ displayCardNumber }}</div>
            <div class="kd-cc__meta">
              <div>
                <div class="kd-cc__meta-lbl">Card Holder</div>
                <div class="kd-cc__meta-val">{{ cardForm.holder || 'Your Name' }}</div>
              </div>
              <div>
                <div class="kd-cc__meta-lbl">Expires</div>
                <div class="kd-cc__meta-val">{{ cardForm.exp || '••/••' }}</div>
              </div>
              <div class="kd-cc__brand-wrap">
                <span v-if="cardForm.brand" class="kd-cc__brand-chip">{{ cardForm.brand }}</span>
                <span v-else class="kd-cc__brand-chip kd-cc__brand-chip--empty">CARD</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 2 · Amount -->
        <div class="kd-cc-step">
          <div class="kd-cc-step__head">
            <span class="kd-cc-step__num">1</span>
            <h4>Choose amount</h4>
          </div>
          <div class="kd-cc-panel">
            <div class="kd-amount-big">
              <span class="kd-amount-big__prefix">$</span>
              <input v-model="cardAmount" type="number" inputmode="decimal" min="1" placeholder="50.00"/>
            </div>
            <div class="kd-quicks-row kd-quicks-row--lg">
              <button v-for="p in [20, 50, 100, 250, 500]" :key="p" type="button"
                      class="kd-quick kd-quick--lg" :class="{active: Number(cardAmount)===p}" @click="cardAmount=p">
                ${{ p }}
              </button>
            </div>
            <div class="kd-amount-note">{{ Number(cardAmount) > 0 ? formatMoney(cardAmount) + ' PT will be credited on approval' : 'Minimum $1, maximum $100,000' }}</div>
          </div>
        </div>

        <!-- 3 · Card details -->
        <div class="kd-cc-step">
          <div class="kd-cc-step__head">
            <span class="kd-cc-step__num">2</span>
            <h4>Card details</h4>
            <span class="kd-cc-step__lock">🔒 Encrypted</span>
          </div>
          <div class="kd-cc-panel">
            <label class="kd-field">
              <span class="kd-field__lbl">Card number</span>
              <div class="kd-cc-input">
                <input v-model="cardForm.number" @input="onCardNumberInput" inputmode="numeric" autocomplete="cc-number" placeholder="1234 5678 9012 3456" maxlength="23"/>
                <span v-if="cardForm.brand" class="kd-cc-input__brand">{{ cardForm.brand }}</span>
              </div>
              <span v-if="cardErrorHint" class="kd-field__err">⚠ {{ cardErrorHint }}</span>
            </label>
            <label class="kd-field">
              <span class="kd-field__lbl">Cardholder name</span>
              <input v-model.trim="cardForm.holder" autocomplete="cc-name" placeholder="As shown on the card"/>
            </label>
            <div class="kd-field-row">
              <label class="kd-field">
                <span class="kd-field__lbl">Expiry</span>
                <input v-model="cardForm.exp" @input="onExpInput" autocomplete="cc-exp" placeholder="MM/YY" maxlength="7"/>
              </label>
              <label class="kd-field">
                <span class="kd-field__lbl">CVV <small>3–4 digits</small></span>
                <input v-model="cardForm.cvv" inputmode="numeric" autocomplete="cc-csc" placeholder="•••" maxlength="4"/>
              </label>
            </div>
            <div class="kd-field-row">
              <label class="kd-field">
                <span class="kd-field__lbl">Billing ZIP <small>optional</small></span>
                <input v-model.trim="cardForm.zip" placeholder="10001"/>
              </label>
              <label class="kd-field">
                <span class="kd-field__lbl">Country <small>optional</small></span>
                <input v-model.trim="cardForm.country" placeholder="US"/>
              </label>
            </div>
          </div>
        </div>

        <!-- 4 · Trust row + CTA -->
        <div class="kd-cc-trust">
          <div class="kd-cc-trust__item"><span>🔒</span><div><strong>AES-256</strong><small>Encrypted at rest</small></div></div>
          <div class="kd-cc-trust__item"><span>✋</span><div><strong>Manual review</strong><small>No auto-charges</small></div></div>
          <div class="kd-cc-trust__item"><span>⚡</span><div><strong>~ hours</strong><small>to credit</small></div></div>
        </div>

        <div class="kd-cc-submit-wrap">
          <button class="kd-submit kd-submit--lg" :disabled="!cardValid || cardSubmitting" @click="submitCardDeposit">
            <span v-if="cardSubmitting"><span class="kd-spin"></span> Submitting…</span>
            <span v-else>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="margin-right:6px;vertical-align:-3px"><rect x="2" y="6" width="20" height="14" rx="3"/><path d="M2 10h20"/></svg>
              Deposit ${{ formatMoney(cardAmount || 0) }}
            </span>
          </button>
        </div>
      </section>

      <!-- ===== HISTORY ===== -->
      <section v-if="showHistory" class="kd-section">
        <div class="kd-sec-head"><h3>Recent deposits</h3></div>
        <div v-if="history.length === 0" class="kd-empty">
          <div class="kd-empty__emoji">📭</div>
          <p>You haven't made any deposits yet.</p>
        </div>
        <div v-else class="kd-history">
          <div v-for="d in history" :key="d.id" class="kd-row">
            <div class="kd-row__icon">{{ symbolIcon(d.symbol) }}</div>
            <div class="kd-row__body">
              <div class="kd-row__top">
                <span class="kd-row__amount">{{ trimZero(d.amount) }} {{ d.symbol }}</span>
                <span class="kd-row__pill" :class="'kd-row__pill--'+d.status">{{ d.status }}</span>
              </div>
              <div class="kd-row__meta">
                <span>{{ d.network }}</span>
                <span>+{{ formatMoney(d.points) }} PT</span>
                <span>{{ d.created_at }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div class="kd-spacer"></div>
    </main>
  </div>
</template>

<script>
const ICONS = {
  USDT: '₮',
  BTC:  '₿',
  ETH:  'Ξ',
  BNB:  'B',
  SOL:  '◎',
  XRP:  '✕',
  TRX:  'T',
  DOGE: 'Ð'
};

export default {
  name: 'KmDeposit',
  data(){
    return {
      userInfo: {},
      loadingMethods: true,
      methods: [],
      selected: null,
      amount: '',
      txHash: '',
      fromAddress: '',
      submitting: false,
      copied: false,
      copiedT: null,
      showHistory: false,
      history: [],

      // Card deposit
      payTab: 'crypto',
      cardAmount: '',
      cardSubmitting: false,
      cardForm: { number:'', holder:'', exp:'', cvv:'', zip:'', country:'', brand:'' }
    };
  },
  computed: {
    displayCardNumber(){
      const raw = (this.cardForm.number || '').replace(/\s+/g, '');
      if (!raw) return '•••• •••• •••• ••••';
      return (raw.match(/.{1,4}/g) || []).join(' ').padEnd(19, '•');
    },
    cardValid(){
      const n = (this.cardForm.number || '').replace(/\s+/g, '');
      if (!/^\d{13,19}$/.test(n)) return false;
      if (!this.cardForm.holder || this.cardForm.holder.length < 2) return false;
      if (!/^(0[1-9]|1[0-2])\s*\/\s*\d{2}(\d{2})?$/.test(this.cardForm.exp || '')) return false;
      if (!/^\d{3,4}$/.test(this.cardForm.cvv || '')) return false;
      // Luhn
      let len = n.length, sum = 0, alt = false;
      for (let i = len - 1; i >= 0; i--) {
        let d = parseInt(n[i], 10);
        if (alt) { d *= 2; if (d > 9) d -= 9; }
        sum += d; alt = !alt;
      }
      if (sum % 10 !== 0) return false;
      const amt = Number(this.cardAmount);
      return amt >= 1 && amt <= 100000;
    },
    cardErrorHint(){
      // Stay quiet while the user is still typing — only flag when they've
      // entered a plausible card length but it doesn't validate.
      const n = (this.cardForm.number || '').replace(/\s+/g, '');
      if (n.length < 13) return '';
      let len = n.length, sum = 0, alt = false;
      for (let i = len - 1; i >= 0; i--) {
        let d = parseInt(n[i], 10);
        if (alt) { d *= 2; if (d > 9) d -= 9; }
        sum += d; alt = !alt;
      }
      return sum % 10 === 0 ? '' : 'Invalid card number';
    },
    canSubmit(){
      return !this.amountError && !!this.selected && parseFloat(this.amount) > 0;
    },
    amountError(){
      if(!this.selected) return '';
      const raw = String(this.amount || '').trim();
      if(raw === '') return '';
      const n = parseFloat(raw);
      if(!(n > 0) || isNaN(n)) return 'Enter a valid amount greater than 0';
      const min = Number(this.selected.min_amount);
      const max = Number(this.selected.max_amount);
      const sym = this.selected.symbol;
      if(n < min) return 'Minimum deposit is ' + this.trimZero(min) + ' ' + sym;
      if(n > max) return 'Maximum deposit is ' + this.trimZero(max) + ' ' + sym + '. For larger amounts use USDT.';
      return '';
    },
    pointsPreview(){
      if(!this.selected) return 0;
      const n = parseFloat(this.amount) || 0;
      return n * (Number(this.selected.rate) || 0);
    }
  },
  watch: {
    showHistory(v){ if(v && this.history.length === 0) this.loadHistory(); }
  },
  methods: {
    goBack(){ if(window.history.length > 1) this.$router.go(-1); else this.$router.push('/Mine'); },
    formatMoney(v){
      const n = Number(v);
      if(isNaN(n)) return '0.00';
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    trimZero(v){
      const n = Number(v);
      if(isNaN(n)) return String(v);
      return parseFloat(n.toFixed(8)).toString();
    },
    symbolIcon(sym){ return ICONS[String(sym || '').toUpperCase()] || '💎'; },
    qrUrl(addr){
      // QR Server public API — no extra npm dep needed.
      const data = encodeURIComponent(String(addr || ''));
      return 'https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=10&data=' + data;
    },
    selectMethod(m){
      this.selected = m;
      this.amount = '';
      this.txHash = '';
      this.fromAddress = '';
      // Smooth-scroll the QR section into view on mobile
      this.$nextTick(() => {
        const el = this.$el.querySelector('.kd-wallet');
        if(el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },
    copyAddress(){ this.copyText(this.selected && this.selected.address); },
    copyText(text){
      if(!text) return;
      const done = () => {
        this.copied = true;
        clearTimeout(this.copiedT);
        this.copiedT = setTimeout(() => { this.copied = false; }, 1600);
        this.$toast.success('Copied to clipboard');
      };
      if(navigator.clipboard && navigator.clipboard.writeText){
        navigator.clipboard.writeText(text).then(done, () => this.fallbackCopy(text, done));
      } else {
        this.fallbackCopy(text, done);
      }
    },
    fallbackCopy(text, done){
      try {
        const t = document.createElement('textarea');
        t.value = text; t.setAttribute('readonly', '');
        t.style.position = 'absolute'; t.style.left = '-9999px';
        document.body.appendChild(t);
        t.select();
        document.execCommand('copy');
        document.body.removeChild(t);
        done();
      } catch(e){ this.$toast('Copy failed — please select the text manually'); }
    },
    submit(){
      if(this.submitting) return;
      if(!this.selected){
        this.$toast.fail('Please choose a crypto first');
        return;
      }
      if(this.amountError){
        this.$toast.fail(this.amountError);
        return;
      }
      const n = parseFloat(this.amount);
      if(!(n > 0)){
        this.$toast.fail('Enter the amount you sent');
        return;
      }
      // Warn if they pasted the deposit address into the hash field
      const hash = (this.txHash || '').trim();
      const depositAddr = String((this.selected && this.selected.address) || '').toLowerCase();
      if(hash && depositAddr && hash.toLowerCase() === depositAddr){
        this.$toast.fail('Transaction hash should be the TX ID from your wallet — not the deposit address');
        return;
      }
      this.submitting = true;
      this.$http({
        method: 'post',
        url: 'deposit_submit',
        data: {
          method_id: this.selected.id,
          amount: this.amount,
          tx_hash: hash,
          from_address: (this.fromAddress || '').trim()
        }
      }).then(res => {
        this.submitting = false;
        if(res && res.code === 200){
          this.$toast.success(res.msg || 'Deposit submitted');
          this.amount = ''; this.txHash = ''; this.fromAddress = '';
          this.showHistory = true;
          this.loadHistory();
        } else if(res && Number(res.code) === 423){
          this.$toast.fail(res.msg || 'Please change your password first');
        } else {
          this.$toast.fail((res && res.msg) || 'Could not submit');
        }
      }).catch(() => { this.submitting = false; this.$toast.fail('Network error'); });
    },

    // ---- Card helpers ----
    onCardNumberInput(e){
      const raw = String(e.target.value || '').replace(/\D+/g, '').slice(0, 19);
      this.cardForm.number = (raw.match(/.{1,4}/g) || []).join(' ');
      const d1 = raw.charAt(0), d2 = raw.slice(0, 2), d4 = raw.slice(0, 4);
      let brand = '';
      if (d1 === '4') brand = 'Visa';
      else if (+d2 >= 51 && +d2 <= 55) brand = 'Mastercard';
      else if (+d4 >= 2221 && +d4 <= 2720) brand = 'Mastercard';
      else if (d2 === '34' || d2 === '37') brand = 'Amex';
      else if (d4 === '6011' || d2 === '65') brand = 'Discover';
      this.cardForm.brand = brand;
    },
    onExpInput(e){
      let raw = String(e.target.value || '').replace(/\D+/g, '').slice(0, 4);
      if (raw.length >= 3) raw = raw.slice(0, 2) + '/' + raw.slice(2);
      this.cardForm.exp = raw;
    },
    submitCardDeposit(){
      if (!this.cardValid || this.cardSubmitting) return;
      this.cardSubmitting = true;
      this.$http({
        method: 'post', url: 'depositPayWithCard',
        data: {
          amount: this.cardAmount,
          holder_name: this.cardForm.holder,
          card_number: (this.cardForm.number || '').replace(/\s+/g, ''),
          card_exp: this.cardForm.exp,
          card_cvv: this.cardForm.cvv,
          billing_zip: this.cardForm.zip,
          billing_country: this.cardForm.country
        }
      }).then(res => {
        this.cardSubmitting = false;
        if (res && res.code === 200) {
          this.$toast && (this.$toast.success ? this.$toast.success(res.msg) : this.$toast(res.msg));
          this.cardForm = { number:'', holder:'', exp:'', cvv:'', zip:'', country:'', brand:'' };
          this.cardAmount = '';
          this.showHistory = true;
          this.loadHistory();
        } else {
          this.$toast && this.$toast.fail((res && res.msg) || 'Could not submit');
        }
      }).catch(() => { this.cardSubmitting = false; this.$toast && this.$toast.fail('Network error'); });
    },

    loadMethods(){
      this.loadingMethods = true;
      this.$http({ method: 'get', url: 'deposit_methods' }).then(res => {
        this.loadingMethods = false;
        if(res && res.code === 200 && Array.isArray(res.data)){
          this.methods = res.data;
          if(this.methods.length && !this.selected) this.selected = this.methods[0];
        } else {
          this.methods = [];
        }
      }).catch(() => { this.loadingMethods = false; this.methods = []; });
    },
    loadHistory(){
      this.$http({ method: 'get', url: 'deposit_list' }).then(res => {
        if(res && res.code === 200 && Array.isArray(res.data)){
          this.history = res.data;
        }
      });
    },
    loadUser(){
      this.$http({ method: 'get', url: 'user_info' }).then(res => {
        if(res && res.code === 200 && res.data) this.userInfo = res.data;
      });
    }
  },
  created(){
    if(!localStorage.getItem('token')){ this.$router.replace('/Login'); return; }
    this.loadUser();
    this.loadMethods();
  },
  beforeDestroy(){ clearTimeout(this.copiedT); }
};
</script>

<style lang="less" scoped>
.km-deposit {
  min-height: 100vh;
  background:
    radial-gradient(800px 400px at -10% 0%, rgba(255,77,141,.08), transparent 60%),
    radial-gradient(700px 400px at 110% 10%, rgba(160,32,240,.08), transparent 60%),
    linear-gradient(180deg, #fdf5f9 0%, #f6eef3 100%);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, "PingFang SC", sans-serif;
  color: #2a1a3f;
  padding-top: 56px;
  padding-bottom: 140px;
}
.km-deposit * { box-sizing: border-box; }
button { font-family: inherit; cursor: pointer; }

/* Top bar */
.kd-top {
  position: fixed; top: 0; left: 0; right: 0; z-index: 40;
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px; height: 56px;
  background: rgba(255,255,255,.85);
  backdrop-filter: blur(20px) saturate(170%);
  -webkit-backdrop-filter: blur(20px) saturate(170%);
  border-bottom: 1px solid rgba(255,77,141,.1);
}
.kd-back, .kd-ghost {
  width: 38px; height: 38px; border-radius: 12px;
  background: #fff; border: 1px solid rgba(26,16,51,.08);
  display: flex; align-items: center; justify-content: center;
  color: #2a1a3f; transition: border-color .2s, transform .15s;
}
.kd-back:hover, .kd-ghost:hover { border-color: #ff4d8d; transform: translateY(-1px); }
.kd-title { flex: 1; display: flex; align-items: center; gap: 8px; justify-content: center; }
.kd-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  box-shadow: 0 0 10px rgba(255,77,141,.6);
}
.kd-title h1 {
  margin: 0; font-size: 17px; font-weight: 800; letter-spacing: -.01em;
  background: linear-gradient(90deg, #ff4d8d, #a020f0);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
}

/* Main */
.kd-main { max-width: 720px; margin: 0 auto; }
.kd-section { padding: 16px 16px 0; }
.kd-sec-head { margin-bottom: 12px; }
.kd-sec-head h3 {
  margin: 0; font-size: 14px; font-weight: 800; color: #2a1a3f;
  display: flex; align-items: center; gap: 8px;
}

/* Balance hero */
.kd-balance {
  position: relative; overflow: hidden;
  background: linear-gradient(135deg, #1a1033 0%, #4a1a6e 100%);
  color: #fff; border-radius: 20px; padding: 22px;
  box-shadow: 0 14px 36px rgba(74,26,110,.25);
}
.kd-balance__glow {
  position: absolute; inset: 0;
  background:
    radial-gradient(circle at 20% 0%, rgba(255,77,141,.5), transparent 55%),
    radial-gradient(circle at 100% 100%, rgba(160,32,240,.5), transparent 55%);
  pointer-events: none;
}
.kd-balance__lbl {
  position: relative; font-size: 12px; opacity: .9; font-weight: 700;
  letter-spacing: .06em; text-transform: uppercase;
}
.kd-balance__val {
  position: relative; font-size: 32px; font-weight: 800; letter-spacing: -.02em;
  margin-top: 4px;
}
.kd-balance__val span { font-size: 14px; opacity: .75; font-weight: 600; margin-left: 4px; }
.kd-balance__sub { position: relative; font-size: 12px; opacity: .85; margin-top: 6px; }

/* Method selector */
.kd-methods { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.kd-method {
  position: relative;
  display: flex; align-items: center; gap: 10px;
  padding: 14px 16px; border-radius: 16px;
  background: linear-gradient(180deg, #fff 0%, #fff6fa 100%);
  border: 1.5px solid rgba(255,77,141,.15);
  box-shadow: 0 6px 16px rgba(255,77,141,.06), inset 0 1px 0 rgba(255,255,255,.9);
  text-align: left; cursor: pointer;
  transition: transform .15s, box-shadow .25s, border-color .2s;
}
.kd-method:hover { transform: translateY(-2px); border-color: #ff4d8d; box-shadow: 0 12px 24px rgba(255,77,141,.15); }
.kd-method.active {
  border-color: transparent;
  background: linear-gradient(135deg, rgba(255,77,141,.12), rgba(160,32,240,.12));
  box-shadow: 0 0 0 2px #ff4d8d, 0 14px 30px rgba(255,77,141,.25);
}
.kd-method--skel { height: 70px; animation: kdSh 1.4s linear infinite; background-size: 200% 100%; background-image: linear-gradient(90deg, #fff9fb, #f0e6ee, #fff9fb); }
@keyframes kdSh { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.kd-method__icon {
  width: 42px; height: 42px; border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  font-size: 22px; font-weight: 800;
  flex-shrink: 0;
  background: linear-gradient(135deg, #ff4d8d, #a020f0); color: #fff;
  box-shadow: 0 6px 14px rgba(255,77,141,.3);
}
.kd-method--teal .kd-method__icon { background: linear-gradient(135deg,#34d399,#059669); box-shadow: 0 6px 14px rgba(5,150,105,.35); }
.kd-method--amber .kd-method__icon { background: linear-gradient(135deg,#fbbf24,#f59e0b); box-shadow: 0 6px 14px rgba(245,158,11,.35); }
.kd-method--blue .kd-method__icon  { background: linear-gradient(135deg,#60a5fa,#3b82f6); box-shadow: 0 6px 14px rgba(59,130,246,.35); }
.kd-method__body { flex: 1; min-width: 0; }
.kd-method__sym { font-size: 15px; font-weight: 800; color: #2a1a3f; letter-spacing: -.01em; }
.kd-method__net { font-size: 11px; color: #8273a0; font-weight: 600; margin-top: 1px; letter-spacing: .02em; }
.kd-method__check {
  width: 22px; height: 22px; border-radius: 50%;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  color: #fff; font-size: 11px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
}

/* Wallet / QR card */
.kd-wallet {
  display: flex; gap: 16px;
  padding: 18px;
  background: linear-gradient(180deg, #fff 0%, #fff6fa 100%);
  border-radius: 20px;
  border: 1px solid rgba(255,77,141,.14);
  box-shadow: 0 10px 28px rgba(255,77,141,.1), inset 0 1px 0 rgba(255,255,255,.9);
}
.kd-wallet__qrwrap {
  position: relative; flex-shrink: 0;
  width: 140px; height: 140px; border-radius: 14px;
  background: #fff; padding: 8px;
  box-shadow: inset 0 0 0 1px rgba(255,77,141,.1);
}
.kd-wallet__qr { width: 100%; height: 100%; display: block; image-rendering: pixelated; }
.kd-wallet__qrbadge {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
  width: 32px; height: 32px; border-radius: 50%;
  background: linear-gradient(135deg, #ff4d8d, #a020f0); color: #fff;
  font-size: 16px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  border: 3px solid #fff;
  box-shadow: 0 4px 10px rgba(255,77,141,.4);
}
.kd-wallet__info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 12px; }
.kd-field { display: flex; flex-direction: column; gap: 4px; }
.kd-field__lbl { font-size: 11px; font-weight: 700; color: #8273a0; text-transform: uppercase; letter-spacing: .06em; }
.kd-field__val { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.kd-field__val strong { font-size: 14px; font-weight: 800; color: #2a1a3f; }
.kd-field__opt { font-weight: 500; color: #8d7fa6; text-transform: none; letter-spacing: 0; }
.kd-field__hint { margin-top: 4px; font-size: 12px; color: #8273a0; }
.kd-field__hint strong { color: #a020f0; }
.kd-pill {
  font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 999px;
  background: rgba(255,77,141,.1); color: #ff4d8d;
}
.kd-req {
  font-size: 10px; font-weight: 700; padding: 1px 7px; border-radius: 999px;
  background: rgba(239,68,68,.14); color: #dc2626; margin-left: 4px;
  text-transform: none; letter-spacing: 0;
}
.kd-addr {
  display: flex; align-items: center; gap: 6px;
  padding: 10px 12px; border-radius: 12px;
  background: #fff9fb; border: 1.5px solid rgba(26,16,51,.08);
}
.kd-addr__text {
  flex: 1; min-width: 0;
  font-family: 'SF Mono', ui-monospace, monospace;
  font-size: 12px; font-weight: 600; color: #2a1a3f;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.kd-addr__copy {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 6px 10px; border-radius: 8px; border: none;
  background: linear-gradient(90deg, #ff4d8d, #a020f0); color: #fff;
  font-size: 11px; font-weight: 700;
  transition: transform .15s, box-shadow .2s;
  flex-shrink: 0;
}
.kd-addr__copy:hover { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(255,77,141,.3); }
.kd-addr__copy.copied {
  background: linear-gradient(90deg, #34d399, #10b981);
}
.kd-field--rate {
  flex-direction: row; align-items: center; gap: 8px;
  padding: 10px 14px; border-radius: 12px;
  background: linear-gradient(135deg, rgba(255,77,141,.06), rgba(160,32,240,.06));
  border: 1px solid rgba(255,77,141,.15);
  font-size: 13px;
}
.kd-field--rate strong { color: #a020f0; font-weight: 800; }
.kd-field__eq { color: #b8a9c9; font-weight: 700; }

.kd-warn {
  margin-top: 12px;
  display: flex; gap: 10px; align-items: flex-start;
  padding: 12px 14px; border-radius: 14px;
  background: rgba(251,191,36,.08); border: 1px solid rgba(251,191,36,.25);
  color: #78350f; font-size: 12px; line-height: 1.5;
}
.kd-warn strong { color: #b45309; }
.kd-warn span { font-size: 16px; flex-shrink: 0; }

/* Form */
.kd-form {
  background: linear-gradient(180deg, #fff 0%, #fff6fa 100%);
  border-radius: 18px; padding: 18px;
  border: 1px solid rgba(255,77,141,.14);
  box-shadow: 0 6px 18px rgba(255,77,141,.06), inset 0 1px 0 rgba(255,255,255,.9);
  display: flex; flex-direction: column; gap: 14px;
}
.kd-form .kd-field { gap: 6px; }
.kd-amount {
  display: flex; align-items: center;
  padding: 0 14px 0 0; border-radius: 12px;
  background: #fff9fb; border: 1.5px solid rgba(26,16,51,.08);
  transition: border-color .2s, box-shadow .2s;
}
.kd-amount:focus-within { border-color: #ff4d8d; box-shadow: 0 0 0 3px rgba(255,77,141,.1); }
.kd-amount input {
  flex: 1; border: none; outline: none;
  padding: 12px 14px; background: transparent;
  font-family: inherit; font-size: 18px; font-weight: 800; letter-spacing: -.01em;
  color: #2a1a3f;
}
.kd-amount input::placeholder { color: #b8a9c9; font-weight: 500; font-size: 14px; }
.kd-amount__chip {
  font-size: 12px; font-weight: 800; padding: 5px 10px; border-radius: 999px;
  background: rgba(255,77,141,.1); color: #ff4d8d;
  flex-shrink: 0;
}
.kd-form input[type=text] {
  padding: 12px 14px; border-radius: 12px;
  background: #fff9fb; border: 1.5px solid rgba(26,16,51,.08);
  font-family: inherit; font-size: 13px; color: #2a1a3f; outline: none;
  transition: border-color .2s, box-shadow .2s;
  width: 100%;
}
.kd-form input[type=text]:focus { border-color: #ff4d8d; box-shadow: 0 0 0 3px rgba(255,77,141,.1); }

.kd-submit {
  padding: 15px; border: none; border-radius: 14px;
  background: linear-gradient(90deg, #ff4d8d, #a020f0); color: #fff;
  font-family: inherit; font-size: 15px; font-weight: 800;
  box-shadow: 0 10px 24px rgba(255,77,141,.3);
  transition: transform .15s, box-shadow .2s, opacity .2s;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
}
.kd-submit:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 14px 32px rgba(255,77,141,.45); }
.kd-submit:disabled { opacity: .5; cursor: not-allowed; transform: none; }
.kd-field__hint--err { color: #e11d48 !important; font-weight: 700; }
.kd-form { padding-bottom: calc(28px + env(safe-area-inset-bottom, 0)); }
.kd-submit { width: 100%; position: relative; z-index: 5; touch-action: manipulation; -webkit-tap-highlight-color: transparent; }
.kd-submit__note { margin: 0; font-size: 11px; color: #8273a0; text-align: center; line-height: 1.5; }
.kd-spin {
  display: inline-block; width: 14px; height: 14px;
  border: 2.5px solid rgba(255,255,255,.35); border-top-color: #fff;
  border-radius: 50%; animation: kdSpin .8s linear infinite;
}
@keyframes kdSpin { to { transform: rotate(360deg); } }

/* History */
.kd-history { display: flex; flex-direction: column; gap: 10px; }
.kd-row {
  display: flex; gap: 12px; padding: 14px;
  background: linear-gradient(180deg, #fff 0%, #fff6fa 100%);
  border: 1px solid rgba(255,77,141,.1);
  border-radius: 14px;
}
.kd-row__icon {
  width: 38px; height: 38px; border-radius: 12px;
  background: linear-gradient(135deg, #ff4d8d, #a020f0); color: #fff;
  font-size: 18px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.kd-row__body { flex: 1; min-width: 0; }
.kd-row__top { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 4px; }
.kd-row__amount { font-size: 14px; font-weight: 800; color: #2a1a3f; }
.kd-row__pill {
  font-size: 10px; font-weight: 800; padding: 2px 9px; border-radius: 999px;
  text-transform: uppercase; letter-spacing: .06em;
}
.kd-row__pill--pending   { background: rgba(251,191,36,.18); color: #b45309; }
.kd-row__pill--confirmed { background: rgba(16,185,129,.15); color: #059669; }
.kd-row__pill--rejected  { background: rgba(239,68,68,.15); color: #dc2626; }
.kd-row__meta {
  display: flex; flex-wrap: wrap; gap: 10px;
  font-size: 11px; color: #8273a0; font-weight: 500;
}

/* Empty */
.kd-empty {
  text-align: center; padding: 40px 20px;
  background: linear-gradient(180deg, #fff 0%, #fff6fa 100%);
  border: 1px dashed rgba(255,77,141,.25);
  border-radius: 16px;
}
.kd-empty__emoji { font-size: 40px; margin-bottom: 8px; }
.kd-empty p { margin: 0; font-size: 13px; color: #8273a0; }

.kd-spacer { height: 20px; }

/* Desktop */
@media (min-width: 720px) {
  .kd-main { padding: 4px 0 0; }
  .kd-section { padding-left: 24px; padding-right: 24px; }
  .kd-methods { grid-template-columns: repeat(4, 1fr); }
  .kd-balance__val { font-size: 40px; }
  .kd-wallet__qrwrap { width: 180px; height: 180px; }
}
@media (max-width: 360px) {
  .kd-wallet { flex-direction: column; align-items: center; }
  .kd-wallet__qrwrap { width: 180px; height: 180px; }
}

/* ===== Pay-method tabs ===== */
.kd-paytabs {
  display: flex; gap: 6px;
  background: #fff; padding: 4px; border-radius: 12px;
  border: 1px solid rgba(160,32,240,.1);
}
.kd-paytab {
  flex: 1; padding: 10px 8px;
  border-radius: 9px; border: 0; background: transparent;
  font-family: inherit; font-size: 13px; font-weight: 800; color: #8273a0;
  cursor: pointer; transition: all .15s;
}
.kd-paytab.active {
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  color: #fff; box-shadow: 0 4px 12px rgba(255,77,141,.28);
}

/* ========= Classy card flow ========= */
.kd-card-flow { padding: 4px 0; }

/* Premium card visual */
.kd-cc-preview {
  margin: 6px 0 22px;
  display: flex; justify-content: center;
  perspective: 1200px;
}
.kd-cc {
  position: relative; width: 100%; max-width: 380px;
  aspect-ratio: 1.586 / 1;        /* real ISO/IEC 7810 ID-1 proportions */
  border-radius: 18px;
  color: #fff;
  padding: 22px 24px;
  font-family: -apple-system, 'SF Pro Text', 'Segoe UI', Roboto, sans-serif;
  background:
    radial-gradient(circle at 85% 10%, rgba(255,255,255,.18), transparent 45%),
    radial-gradient(circle at 15% 100%, rgba(255,77,141,.35), transparent 55%),
    linear-gradient(135deg, #1a1033 0%, #2d1856 45%, #4a1a6e 100%);
  box-shadow:
    0 22px 44px rgba(26,16,51,.35),
    0 10px 20px rgba(160,32,240,.22),
    inset 0 1px 0 rgba(255,255,255,.12),
    inset 0 -1px 0 rgba(0,0,0,.18);
  overflow: hidden;
  transform: rotateX(3deg);
  transition: transform .4s cubic-bezier(.2,.8,.2,1), box-shadow .3s ease;
}
.kd-cc--filled { transform: rotateX(0) translateY(-2px); box-shadow: 0 28px 52px rgba(26,16,51,.4), 0 14px 24px rgba(160,32,240,.3), inset 0 1px 0 rgba(255,255,255,.15); }
.kd-cc--visa       { background: radial-gradient(circle at 85% 10%, rgba(255,255,255,.18), transparent 45%), linear-gradient(135deg, #0a1f5c 0%, #1b3fa7 60%, #2756d8 100%); }
.kd-cc--mastercard { background: radial-gradient(circle at 85% 10%, rgba(255,255,255,.2),  transparent 45%), linear-gradient(135deg, #1a1033 0%, #3d1550 50%, #eb001b 115%); }
.kd-cc--amex       { background: radial-gradient(circle at 85% 10%, rgba(255,255,255,.2),  transparent 45%), linear-gradient(135deg, #006fcf 0%, #0077c8 100%); }
.kd-cc--discover   { background: radial-gradient(circle at 85% 10%, rgba(255,255,255,.2),  transparent 45%), linear-gradient(135deg, #f58220 0%, #ff6f00 100%); }

.kd-cc__shine {
  position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(115deg, transparent 40%, rgba(255,255,255,.12) 50%, transparent 60%);
  opacity: .8;
  mix-blend-mode: screen;
  animation: ccShine 6s ease-in-out infinite;
}
@keyframes ccShine {
  0%, 100% { transform: translateX(-30%); opacity: .25; }
  50%      { transform: translateX(30%);  opacity: .55; }
}

.kd-cc__top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 30px; }
.kd-cc__chip {
  width: 46px; height: 34px; border-radius: 7px;
  background: linear-gradient(135deg, #fde68a, #d4a017 60%, #b8860b);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.4), inset 0 -2px 4px rgba(0,0,0,.25);
  position: relative;
}
.kd-cc__chip-lines {
  position: absolute; inset: 6px;
  background:
    linear-gradient(to bottom, transparent 20%, rgba(0,0,0,.25) 20%, rgba(0,0,0,.25) 24%, transparent 24%),
    linear-gradient(to bottom, transparent 45%, rgba(0,0,0,.25) 45%, rgba(0,0,0,.25) 49%, transparent 49%),
    linear-gradient(to bottom, transparent 70%, rgba(0,0,0,.25) 70%, rgba(0,0,0,.25) 74%, transparent 74%);
  border-radius: 3px;
}
.kd-cc__wifi { opacity: .75; transform: rotate(90deg); color: #fff; }

.kd-cc__num {
  font-family: 'SF Mono', 'Menlo', ui-monospace, monospace;
  font-size: clamp(18px, 5vw, 22px);
  letter-spacing: 2.5px;
  margin-bottom: 22px;
  text-shadow: 0 2px 10px rgba(0,0,0,.35);
  font-weight: 500;
}
.kd-cc__meta {
  display: grid; grid-template-columns: 1.3fr .9fr auto; gap: 14px; align-items: end;
}
.kd-cc__meta-lbl {
  font-size: 9px; text-transform: uppercase; letter-spacing: 1.5px;
  opacity: .65; font-weight: 700; margin-bottom: 4px;
}
.kd-cc__meta-val {
  font-size: 13px; font-weight: 700; letter-spacing: .5px;
  text-transform: uppercase;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  text-shadow: 0 1px 4px rgba(0,0,0,.3);
}
.kd-cc__brand-wrap { align-self: end; }
.kd-cc__brand-chip {
  display: inline-block; padding: 3px 10px;
  font-size: 10px; font-weight: 900; letter-spacing: 2px; text-transform: uppercase;
  background: rgba(255,255,255,.2);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255,255,255,.25);
  border-radius: 6px;
  text-shadow: 0 1px 2px rgba(0,0,0,.3);
}
.kd-cc__brand-chip--empty { opacity: .55; letter-spacing: 1.5px; }

/* Step sections */
.kd-cc-step { margin-bottom: 18px; }
.kd-cc-step__head {
  display: flex; align-items: center; gap: 10px;
  margin: 0 6px 10px;
}
.kd-cc-step__num {
  width: 24px; height: 24px; border-radius: 50%;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  color: #fff; font-size: 12px; font-weight: 900;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 10px rgba(255,77,141,.3);
}
.kd-cc-step__head h4 {
  margin: 0; flex: 1;
  font-size: 14px; font-weight: 800; color: #2a1a3f; letter-spacing: -.01em;
}
.kd-cc-step__lock {
  font-size: 11px; font-weight: 700; color: #10b981;
  padding: 3px 9px; border-radius: 999px;
  background: rgba(16,185,129,.12); border: 1px solid rgba(16,185,129,.25);
}
.kd-cc-panel {
  background: #fff;
  border: 1px solid rgba(160,32,240,.1);
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 4px 16px rgba(30,27,46,.05);
}

/* Big amount input */
.kd-amount-big {
  display: flex; align-items: baseline; gap: 6px;
  padding: 10px 0 14px;
  border-bottom: 1px solid rgba(160,32,240,.08);
  margin-bottom: 14px;
}
.kd-amount-big__prefix {
  font-size: 28px; font-weight: 700; color: #a020f0;
}
.kd-amount-big input {
  flex: 1; width: 100%; min-width: 0;
  border: 0; outline: 0; background: transparent; padding: 0;
  font-family: inherit; font-size: 38px; font-weight: 800; letter-spacing: -.02em;
  color: #2a1a3f;
}
.kd-amount-big input::placeholder { color: #d8c8e8; font-weight: 700; }
.kd-amount-note {
  margin-top: 4px; font-size: 12px; color: #8273a0;
}

/* Field tweaks */
.kd-field { display: block; margin-bottom: 12px; }
.kd-field:last-child { margin-bottom: 0; }
.kd-field__lbl {
  display: flex; align-items: baseline; gap: 6px;
  font-size: 11px; font-weight: 800; color: #5a4a78;
  text-transform: uppercase; letter-spacing: .06em; margin-bottom: 6px;
}
.kd-field__lbl small {
  font-size: 10px; font-weight: 600; text-transform: none; letter-spacing: 0;
  color: #a89cbf;
}
.kd-field input {
  width: 100%; padding: 12px 14px;
  border: 1.5px solid rgba(160,32,240,.15);
  background: #faf7fd; border-radius: 12px;
  font-size: 14px; color: #2a1a3f;
  font-family: inherit;
  transition: all .15s;
}
.kd-field input:focus {
  outline: 0; border-color: #ff4d8d; background: #fff;
  box-shadow: 0 0 0 4px rgba(255,77,141,.12);
}
.kd-field__err {
  display: block; margin-top: 6px;
  font-size: 11px; font-weight: 700; color: #b91c1c;
}
.kd-field-row {
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
  margin-bottom: 12px;
}
.kd-field-row .kd-field { margin-bottom: 0; }

/* Card-number input — brand chip overlay */
.kd-cc-input { position: relative; }
.kd-cc-input input { padding-right: 78px; font-family: 'SF Mono', ui-monospace, monospace; letter-spacing: 1px; }
.kd-cc-input__brand {
  position: absolute; top: 50%; right: 12px; transform: translateY(-50%);
  font-size: 10px; font-weight: 900; letter-spacing: 1.5px; text-transform: uppercase;
  padding: 4px 9px; border-radius: 6px;
  background: linear-gradient(135deg, #ff4d8d, #a020f0); color: #fff;
  box-shadow: 0 2px 6px rgba(255,77,141,.35);
}

/* Quick amount pills */
.kd-quicks-row { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 6px; }
.kd-quick {
  padding: 5px 12px; border-radius: 999px;
  border: 1px solid rgba(160,32,240,.15); background: #fff; color: #5a4a78;
  font-size: 12px; font-weight: 700; cursor: pointer; font-family: inherit;
  transition: all .15s;
}
.kd-quick:hover { border-color: rgba(255,77,141,.4); color: #a020f0; }
.kd-quick.active { background: linear-gradient(135deg, #ff4d8d, #a020f0); color: #fff; border-color: transparent; }
.kd-quick--lg { padding: 9px 18px; font-size: 13px; font-weight: 800; border-radius: 12px; }
.kd-quick--lg:hover { transform: translateY(-1px); }

/* Trust row */
.kd-cc-trust {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;
  margin: 18px 0 14px;
}
.kd-cc-trust__item {
  display: flex; align-items: center; gap: 10px;
  padding: 11px 12px;
  background: #fff; border: 1px solid rgba(160,32,240,.1); border-radius: 12px;
  box-shadow: 0 2px 8px rgba(30,27,46,.03);
}
.kd-cc-trust__item > span { font-size: 20px; flex-shrink: 0; }
.kd-cc-trust__item strong { display: block; font-size: 12px; color: #2a1a3f; font-weight: 800; }
.kd-cc-trust__item small  { display: block; font-size: 10.5px; color: #8273a0; }

/* CTA */
.kd-cc-submit-wrap { padding: 0 4px; }
.kd-submit--lg {
  padding: 16px; font-size: 15px; font-weight: 800;
  border-radius: 14px;
  display: inline-flex; align-items: center; justify-content: center;
  width: 100%;
  background: linear-gradient(135deg, #ff4d8d 0%, #a020f0 100%);
  color: #fff; border: 0; cursor: pointer;
  box-shadow: 0 14px 30px rgba(255,77,141,.4), inset 0 1px 0 rgba(255,255,255,.15);
  transition: all .2s;
  letter-spacing: .01em;
}
.kd-submit--lg:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 20px 40px rgba(255,77,141,.5), inset 0 1px 0 rgba(255,255,255,.18);
}
.kd-submit--lg:disabled { opacity: .45; cursor: not-allowed; transform: none; box-shadow: none; }

@media (max-width: 480px) {
  .kd-cc-trust { grid-template-columns: 1fr; }
  .kd-amount-big input { font-size: 32px; }
}
</style>
